<template>
  <Transition name="modal" @after-leave="onAfterLeave">
    <div
      v-if="visible"
      class="modal-play"
      :class="[`modal-play--${variant}`]"
      role="dialog"
      aria-modal="true"
      @keydown.esc="handleEsc"
    >
      <!-- Backdrop -->
      <div
        class="modal-play__backdrop"
        @click="handleBackdropClick"
      />

      <!-- Container -->
      <div
        ref="containerRef"
        class="modal-play__container"
        :class="[
          `modal-play__container--${variant}`,
          `modal-play__container--${size}`,
          { 'modal-play__container--mobile': isMobile },
        ]"
        tabindex="-1"
      >
        <!-- Header -->
        <header v-if="!hideHeader" class="modal-play__header">
          <div class="modal-play__header__content">
            <div v-if="icon" class="modal-play__header__icon" :style="{ background: `color-mix(in srgb, ${iconColor ?? 'var(--maincolor)'} 12%, transparent)` }">
              <IconGoogle :name="icon" :color="iconColor ?? 'var(--maincolor)'" size="24px" />
            </div>
            <div class="modal-play__header__text">
              <TextPlay
                v-if="title"
                :text="title"
                color="var(--fontcolor)"
                weight="bold"
                :size="variant === 'dialog' ? 'xl' : 'lg'"
              />
              <TextPlay
                v-if="subtitle"
                :text="subtitle"
                color="var(--text-color)"
                size="sm"
              />
            </div>
          </div>
          <button
            type="button"
            class="modal-play__close"
            :aria-label="t('general.close')"
            @click="close"
          >
            <IconGoogle name="close" size="20px" color="var(--text-color)" />
          </button>
        </header>

        <!-- Body -->
        <div class="modal-play__body" :class="{ 'modal-play__body--no-padding': noPadding }">
          <slot />
        </div>

        <!-- Footer -->
        <footer v-if="$slots.footer" class="modal-play__footer">
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLayoutStore } from '@/store/layout.store'
import IconGoogle from '../../atoms/IconGoogle/IconGoogle.vue'
import TextPlay from '../../atoms/TextPlay/TextPlay.vue'

export type ModalVariant = 'dialog' | 'panel' | 'fullscreen'
export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'

interface Props {
  variant?: ModalVariant
  size?: ModalSize
  title?: string
  subtitle?: string
  icon?: string
  iconColor?: string
  hideHeader?: boolean
  noPadding?: boolean
  noCloseOnEsc?: boolean
  noCloseOnBackdrop?: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'dialog',
  size: 'md',
  title: undefined,
  subtitle: undefined,
  icon: undefined,
  iconColor: undefined,
  hideHeader: false,
  noPadding: false,
  noCloseOnEsc: false,
  noCloseOnBackdrop: false,
  loading: false,
})

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const layoutStore = useLayoutStore()

const visible = ref(true)
const containerRef = ref<HTMLElement | null>(null)

const isMobile = computed(() => layoutStore.getIsMobile)

function close() {
  visible.value = false
}

function onAfterLeave() {
  emit('close')
}

function handleEsc() {
  if (!props.noCloseOnEsc) {
    close()
  }
}

function handleBackdropClick() {
  if (!props.noCloseOnBackdrop) {
    close()
  }
}

// Focus trap: focus the container when mounted
onMounted(() => {
  nextTick(() => {
    containerRef.value?.focus()
  })
  document.body.style.overflow = 'hidden'
})

onBeforeUnmount(() => {
  document.body.style.overflow = ''
})

defineExpose({ close })
</script>

<style lang="scss" scoped>
// --- Tokens ---
$sizes-dialog: (
  xs: 400px,
  sm: 520px,
  md: 680px,
  lg: 880px,
  xl: 1080px,
  full: calc(100vw - 2rem),
);

$sizes-panel: (
  xs: 320px,
  sm: 400px,
  md: 520px,
  lg: 680px,
  xl: 880px,
  full: calc(100vw - 4rem),
);

// --- Base ---
.modal-play {
  position: fixed;
  inset: 0;
  z-index: var(--modal-z-index, 100000);
  display: flex;
  align-items: center;
  justify-content: center;

  &--panel {
    justify-content: flex-end;
  }

  &--fullscreen {
    align-items: stretch;
    padding: 1rem;
  }
}

// --- Backdrop ---
.modal-play__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

// --- Container ---
.modal-play__container {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--backgroundcolor);
  border-radius: 16px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
  max-height: calc(100vh - 2rem);
  max-width: calc(100vw - 2rem);
  outline: none;

  // Dialog sizes
  &--dialog {
    @each $name, $width in $sizes-dialog {
      &.modal-play__container--#{$name} {
        width: $width;
      }
    }
  }

  // Panel variant
  &--panel {
    border-radius: 16px 0 0 16px;
    height: 100vh;
    max-height: 100vh;
    max-width: 100vw;

    @each $name, $width in $sizes-panel {
      &.modal-play__container--#{$name} {
        width: $width;
      }
    }
  }

  // Fullscreen variant
  &--fullscreen {
    width: 100%;
    height: 100%;
    max-height: 100%;
    max-width: 100%;
    border-radius: 12px;
  }

  // Mobile override
  &--mobile {
    width: 100vw !important;
    max-width: 100vw;
    border-radius: 16px 16px 0 0;
    max-height: 90vh;
    align-self: flex-end;
  }

  &--mobile.modal-play__container--panel {
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }

  &--mobile.modal-play__container--fullscreen {
    border-radius: 0;
    max-height: 100vh;
  }
}

// --- Header ---
.modal-play__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem 1.5rem 0 1.5rem;
  flex-shrink: 0;

  &__content {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    flex: 1;
    min-width: 0;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    flex-shrink: 0;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
}

.modal-play__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover {
    background: var(--layout-background-color);
  }
}

// --- Body ---
.modal-play__body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  overscroll-behavior: contain;

  &--no-padding {
    padding: 0;
  }
}

// --- Footer ---
.modal-play__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--divider-color);
  flex-shrink: 0;
}

// --- Transitions ---
.modal-enter-active {
  transition: opacity 0.2s ease;

  .modal-play__container--dialog {
    transition: opacity 0.2s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .modal-play__container--panel {
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .modal-play__container--mobile:not(.modal-play__container--panel) {
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
}

.modal-leave-active {
  transition: opacity 0.15s ease;

  .modal-play__container {
    transition: opacity 0.15s ease, transform 0.15s ease;
  }
}

.modal-enter-from {
  opacity: 0;

  .modal-play__container--dialog {
    opacity: 0;
    transform: scale(0.95) translateY(8px);
  }

  .modal-play__container--panel {
    transform: translateX(100%);
  }

  .modal-play__container--mobile:not(.modal-play__container--panel) {
    transform: translateY(100%);
  }
}

.modal-leave-to {
  opacity: 0;

  .modal-play__container--dialog {
    opacity: 0;
    transform: scale(0.97);
  }

  .modal-play__container--panel {
    transform: translateX(100%);
  }

  .modal-play__container--mobile:not(.modal-play__container--panel) {
    transform: translateY(100%);
  }
}
</style>
